import { inject, Injectable } from '@angular/core';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { Navigation } from 'app/core/navigation/navigation.types';
import { BehaviorSubject, catchError, map, Observable, of, tap } from 'rxjs';

import { UserService } from '@app/modules/admin/user/user.service';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _userService = inject(UserService);

    private readonly _emptyNav: Navigation = {
        compact: [],
        default: [],
        futuristic: [],
        horizontal: [],
    };

    private _navigation$ = new BehaviorSubject<Navigation>(this._emptyNav);
    readonly navigation$ = this._navigation$.asObservable();

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    // get navigation$(): Observable<Navigation> {
    //     return this._navigation.asObservable();
    // }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------
    /**
     * Get all navigation data — now directly from backend “allowed menus”.
     * Assumes backend returns the same structure used by Fuse (`children`, `type`, `id`, `title`, `link`, flags, etc.)
     */
    get(): Observable<Navigation> {
        return this._userService.getAllowedMenus().pipe(
            map((items: any) => (Array.isArray(items) ? items : items?.data ?? []) as FuseNavigationItem[]),

            // Prune & normalize just in case (hide non-view items, sort by sortorder)
            map((items) => this._pruneAndSort(items)),

            // If you use only one layout (Compact), reuse it for others;
            // or map to specific layouts if BE splits by layout.
            map((compact) => {
                const nav: Navigation = {
                    compact,
                    default: compact,
                    futuristic: compact,
                    horizontal: compact,
                };
                return nav;
            }),

            tap((navigation) => {
                // Single side-effect point
                this._navigation$.next(navigation);
            }),

            catchError((err) => {
                console.error('Navigation load failed', err);
                this._navigation$.next(this._emptyNav);
                return of(this._emptyNav);
            })
        );
    }

    // ───────────────────────────────────────────────────────────
    // Helpers
    // ───────────────────────────────────────────────────────────
    private _pruneAndSort(items: FuseNavigationItem[]): FuseNavigationItem[] {
        const sortFn = (a: any, b: any) => (a?.sortorder ?? 0) - (b?.sortorder ?? 0);
        const walk = (list: FuseNavigationItem[] | undefined): FuseNavigationItem[] => {
            if (!Array.isArray(list)) return [];
            return (
                list
                    .map((i) => ({
                        ...i,
                        view: !!i.view,
                        add: !!i.add,
                        update: !!i.update,
                        delete: !!i.delete,
                        children: walk(i.children),
                    }))
                    // Keep if view=true OR it has visible descendants
                    .filter((i) => i.view || (i.children && i.children.length > 0))
                    .sort(sortFn)
            );
        };
        return walk(items);
    }
}
